import axios from 'axios';

import { GetUtilisateurs, GetSAVFournisseurDepot, GetLitiges, GetReclamationsSurBonDeVente} from 'types/params';
import { BasicReclamationParam} from 'types/params';
import { GetPackingListsAdministration } from 'types/params';

import { ApiUtilisateurs, ApiSAVFournisseurDepot, ApiLitiges, ApiReclamationsSurBonDeVente, ApiPieceGestionFacture } from 'types/apiReturns';
import { ApiPieceGestionDossiers, ApiCommandeGestion } from 'types/apiReturns';
import { ApiGroupeItem, ApiPieceGestionLigne, ApiPieceAddAction } from 'types/apiReturns';
import { ApiAdministrationCategorie } from 'types/apiReturns';
import { ApiChargerContactExclus, ApiChargerContactExclu } from 'types/apiReturns';
import { RecentCommandeGestion, RecentBonLivraisonGestion, RecentFactureGestion } from 'types/apiReturns';
import { ApiWIServiceResult } from 'types/apiReturns';
import { GroupedErrorMessages, ApplyActionsResult, RabbitMQAction } from 'types/rabbitMQ';
import { IntegrationPiecesGestionEntite, IntegrationPiecesGestionEntiteStats, IntegrationPiecesGestionPeriod } from 'types/integrationPiecesGestion';
import { MonitoringReclamationsAvoir, MonitoringCommandesAvoir } from 'types/monitoringAvoir';
import { MonitoringBonsLivraison } from 'types/monitoringBonsLivraison';
import { ApiPackingListsAdministration } from 'types/apiReturns';


// Requête pour obtenir des utilisateurs pour l'administration
// 
export const fetchUtilisateursAdministration = async (param: GetUtilisateurs) : Promise<ApiUtilisateurs> => {

    const { data } = await axios.post(`/api/utilisateurs/administration`, param);

    return data as ApiUtilisateurs;
};


// Requête pour obtenir des infos sur la liaison SAV fournisseur / dépôt 
// 
export const fetchSAVFournisseurDepot = async (param: GetSAVFournisseurDepot) : Promise<ApiSAVFournisseurDepot> => {

    const { data } = await axios.get(`/api/sav-fournisseurs/${param.fournisseurId}/depot?codeDepot=${param.codeDepot}`);

    return data as ApiSAVFournisseurDepot;
};


// Requête pour obtenir les litiges pour l'administration
//
export const fetchLitigesAdministration = async (param: GetLitiges): Promise<ApiLitiges> => {

    const { data } = await axios.post(`/api/litiges/administration`, param);

    return data as ApiLitiges;
};

// Requête pour obtenir le message JSON RabbitMQ d'un litige
//
export const fetchLitigeMessageJson = async (litigeId: number): Promise<{ messageJson: string | null }> => {

    const { data } = await axios.get(`/api/litiges/${litigeId}/message-json`);

    return data as { messageJson: string | null };
};

// Requête pour lister les réclamations sur bon de vente (RECENT) pour l'administration
//
export const fetchReclamationsSurBonDeVenteAdministration = async (param: GetReclamationsSurBonDeVente): Promise<ApiReclamationsSurBonDeVente> => {

    const { data } = await axios.post(`/api/reclamations-sur-bon-de-vente/administration`, param);

    return data as ApiReclamationsSurBonDeVente;
};

// Requête pour obtenir les packing lists (administration)
//
export const fetchPackingListsAdministration = async (param: GetPackingListsAdministration): Promise<ApiPackingListsAdministration> => {

    const { data } = await axios.get(`/api/packing-lists/administration`, {
        params: {
            page: param.page,
            nbParPage: param.nbParPage,
            filtrePackingListId: param.filtrePackingListId,
            filtreCodeDepot: param.filtreCodeDepot,
            triPar: param.triPar,
            triAscendant: param.triAscendant,
        }
    });

    return data as ApiPackingListsAdministration;
};

// Requête pour obtenir les BLs d'une même facture qu'une pièce et ses litiges
// 
export const fetchPieceGestionFacture = async (numeroPiece: string): Promise<ApiPieceGestionFacture> => {

    const { data } = await axios.get(`/api/pieces-gestion/${numeroPiece}/facture`);

    return data as ApiPieceGestionFacture;
};


// Requête pour obtenir les dossiers d'une pièce gestion
// 
export const fetchPieceGestionDossiers = async (numeroPiece: string): Promise<ApiPieceGestionDossiers> => {

    const { data } = await axios.get(`/api/pieces-gestion/${numeroPiece}/dossiers`);

    return data as ApiPieceGestionDossiers;
};


// Requête pour obtenir une commande gestion par sa clé (onglet Commande de la page Information)
//
export const fetchCommandeGestion = async (cle: string): Promise<ApiCommandeGestion> => {

    const { data } = await axios.get(`/api/commandes-gestion/${cle}`);

    return data as ApiCommandeGestion;
};


// Requête pour obtenir les groupes de traitement actifs et visibles (sélecteur de GT).
//
export const fetchGroupesActifsVisibles = async (): Promise<ApiGroupeItem[]> => {

    // includeActions=false : un sélecteur de GT n'a pas besoin des actions de chaque groupe.
    const { data } = await axios.get(`/api/groupes`, { params: { actif: true, visible: true, includeActions: false } });

    return data as ApiGroupeItem[];
};


// Recherche une pièce par son numéro (mode "addAction") pour l'aperçu des dialogues d'action.
// Renvoie la pièce (numéro, commande d'origine, code client) ou null si elle n'existe pas.
//
export const rechercherPieceAddAction = async (numeroPiece: string): Promise<ApiPieceAddAction | null> => {

    const { data } = await axios.get(`/api/pieces-gestion`, { params: { type: 'piece', numeroPiece, mode: 'addAction' } });

    return Array.isArray(data) && data.length > 0 ? (data[0] as ApiPieceAddAction) : null;
};


// Requête pour obtenir les lignes d'une pièce (aperçu : article, désignation, quantité).
//
export const fetchPieceGestionLignes = async (numeroPiece: string): Promise<ApiPieceGestionLigne[]> => {

    const { data } = await axios.get(`/api/piece-gestion-lignes`, { params: { numeroPiece } });

    return data as ApiPieceGestionLigne[];
};

// Requêtes pour l'administration "Catégories & Qualifications"
//
export const fetchAdministrationCategoriesQualifications = async (): Promise<ApiAdministrationCategorie[]> => {

    const { data } = await axios.get(`/api/administration/categories-qualifications`);

    return data as ApiAdministrationCategorie[];
};

export const updateAdministrationCategorie = async (id: number, payload: { aide: string | null }): Promise<void> => {

    await axios.put(`/api/administration/categories-qualifications/categories/${id}`, payload);
};

export const updateAdministrationQualification = async (id: number, payload: { aide: string | null; impacteQuantiteEnCours: boolean }): Promise<void> => {

    await axios.put(`/api/administration/categories-qualifications/qualifications/${id}`, payload);
};


// Requêtes pour l'administration "Exclusions du chargement des contacts"
//
export const fetchAdministrationChargerContactExclus = async (): Promise<ApiChargerContactExclus> => {

    const { data } = await axios.get(`/api/chargerContactExclus/administration`);

    return data as ApiChargerContactExclus;
};

export const postChargerContactExclu = async (payload: { niveau: number; code: string }): Promise<ApiChargerContactExclu> => {

    const { data } = await axios.post(`/api/chargerContactExclus`, payload);

    return data as ApiChargerContactExclu;
};

export const deleteChargerContactExclu = async (id: number): Promise<void> => {

    await axios.delete(`/api/chargerContactExclus/${id}`);
};


// Requête pour vérifier si une réclamation doit avoir créér un fichier FMCP
// 
export const fetchFMCPFileShouldExist = async (param: BasicReclamationParam): Promise<boolean> => {

    const response = await axios.head(`/api/reclamations/${param.reclamationId}/fmcp-file-should-exist`);

    return response.status === 200;
};


// Requête pour vérifier si le fichier FMCP qu'une réclamation doit avoir créér existe
//
export const fetchFMCPFileExist = async (param: BasicReclamationParam): Promise<boolean> => {

    const response = await axios.head(`/api/reclamations/${param.reclamationId}/fmcp-file-exist`);

    return response.status === 200;
};

//// Requête pour rechercher une commande de gestion
////
//export const fetchCommandeGestion = async (numeroCommande: string, codeClient?: string): Promise<ApiCommandeGestion> => {

//    let url = `/api/commandes-gestion/${encodeURIComponent(numeroCommande)}`;
//    if (codeClient) {
//        url += `?codeClient=${encodeURIComponent(codeClient)}`;
//    }

//    const { data } = await axios.get(url);

//    return data as ApiCommandeGestion;
//};


//// Requêtes pour les listes récentes
////
//export const fetchCommandesGestionRecentes = async (limit: number = 10): Promise<RecentCommandeGestion[]> => {
//    const { data } = await axios.get(`/api/commandes-gestion/recentes?limit=${limit}`);
//    return data as RecentCommandeGestion[];
//};

//export const fetchBonsLivraisonGestionRecents = async (limit: number = 10): Promise<RecentBonLivraisonGestion[]> => {
//    const { data } = await axios.get(`/api/bons-livraison-gestion/recents?limit=${limit}`);
//    return data as RecentBonLivraisonGestion[];
//};

//export const fetchFacturesGestionRecentes = async (limit: number = 10): Promise<RecentFactureGestion[]> => {
//    const { data } = await axios.get(`/api/factures-gestion/recentes?limit=${limit}`);
//    return data as RecentFactureGestion[];
//};


// Requête pour obtenir les messages d'erreur de RabbitMQ
//
export const fetchRabbitMQErrorMessages = async (): Promise<GroupedErrorMessages[]> => {
    const { data } = await axios.get(`/api/rabbitmq/error-messages`);
    return data as GroupedErrorMessages[];
};

// Applique les actions sélectionnées par l'utilisateur sur la file d'erreur RabbitMQ
//
export const applyRabbitMQActions = async (
    actionsById: Record<string, Exclude<RabbitMQAction, 'none'>>
): Promise<ApplyActionsResult> => {
    const { data } = await axios.post(`/api/rabbitmq/apply`, actionsById);
    return data as ApplyActionsResult;
};

// Envoie un message édité manuellement sur une queue donnée
//
export const sendEditedRabbitMQMessage = async (
    params: { queue: string; payload: string }
): Promise<{ message: string }> => {
    const { data } = await axios.post(`/api/rabbitmq/send-edited`, params);
    return data as { message: string };
};

// Statistiques d'intégration et de modification d'UNE entité pièce gestion, ventilées
// par heure (semaine) ou par jour (mois). Un appel par entité (chargement progressif) ;
// passer le même `reference` (ISO 8601) sur tous les appels d'un même affichage pour
// que les buckets soient alignés.
//
export const fetchIntegrationPiecesGestionStats = async (
    entite: IntegrationPiecesGestionEntite,
    period: IntegrationPiecesGestionPeriod,
    reference: string
): Promise<IntegrationPiecesGestionEntiteStats> => {
    const { data } = await axios.get(`/api/administration/integration-pieces-gestion/stats/${entite}`, {
        params: { period, reference }
    });
    return data as IntegrationPiecesGestionEntiteStats;
};

// Monitoring des avoirs (admin "Expérimental") — dernières réclamations ayant eu un avoir.
export const fetchMonitoringReclamationsAvoir = async (
    page: number,
    nbParPage: number
): Promise<MonitoringReclamationsAvoir> => {
    const { data } = await axios.get(`/api/reclamations/monitoring-avoir`, {
        params: { page, nbParPage }
    });
    return data as MonitoringReclamationsAvoir;
};

// Monitoring des avoirs — dernières CommandeGestion rattachées à un dossier (ReclamationId).
export const fetchMonitoringCommandesAvoir = async (
    page: number,
    nbParPage: number
): Promise<MonitoringCommandesAvoir> => {
    const { data } = await axios.get(`/api/commandes-gestion/monitoring-avoir`, {
        params: { page, nbParPage }
    });
    return data as MonitoringCommandesAvoir;
};

// Monitoring des bons de livraison (admin "Expérimental") — BonLivraisonGestion sans
// CommandeGestion correspondante (BL.NumeroPiece = Commande.Cle), hors statuts Annulé/Ordonnancement.
export const fetchMonitoringBonsLivraison = async (
    page: number,
    nbParPage: number
): Promise<MonitoringBonsLivraison> => {
    const { data } = await axios.get(`/api/bons-livraison-gestion/monitoring`, {
        params: { page, nbParPage }
    });
    return data as MonitoringBonsLivraison;
};

// Lecture brute d'une réclamation sur bon de vente (RECENT) — admin "Expérimental".
// Le JSON brut de la réponse Gescom est dans result.message.
export const fetchLectureRecent = async (cle: string): Promise<ApiWIServiceResult> => {
    const { data } = await axios.get(`/api/gescom/recent/${encodeURIComponent(cle)}`);
    return data as ApiWIServiceResult;
};
